import React, { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { CheckCircle2, Circle, Package, MessageCircle } from "lucide-react";
import api, { apiErr } from "@/lib/api";
import { useStore, waLink } from "@/context/StoreContext";
import { toast } from "sonner";

export default function TrackOrder() {
  const [sp] = useSearchParams();
  const { settings } = useStore();
  const [orderNo, setOrderNo] = useState(sp.get("order") || "");
  const [contact, setContact] = useState("");
  const [result, setResult] = useState(null);
  const [busy, setBusy] = useState(false);

  const doTrack = async (e) => {
    e?.preventDefault(); setBusy(true);
    try { const r = await api.post("/track", { order_number: orderNo, contact }); setResult(r.data); }
    catch (er) { toast.error(apiErr(er)); setResult(null); }
    setBusy(false);
  };

  return (
    <div className="max-w-2xl mx-auto px-4 sm:px-8 py-14">
      <div className="text-center mb-8">
        <Package size={40} className="mx-auto text-sharoa-rose" />
        <h1 className="font-serif text-4xl mt-4">TRACK YOUR SHAROA ORDER ♡</h1>
        <p className="text-sharoa-muted mt-2">Your SHAROA journey, from our studio to your doorstep.</p>
      </div>

      <form onSubmit={doTrack} className="bg-sharoa-ivory p-6 space-y-3">
        <input data-testid="track-order-number" value={orderNo} onChange={(e) => setOrderNo(e.target.value)} placeholder="Order Number (e.g. SHR10246)"
          className="w-full border border-sharoa-line bg-white px-4 py-3 text-sm outline-none focus:border-sharoa-rose" />
        <input data-testid="track-contact" value={contact} onChange={(e) => setContact(e.target.value)} placeholder="Mobile Number OR Email"
          className="w-full border border-sharoa-line bg-white px-4 py-3 text-sm outline-none focus:border-sharoa-rose" />
        <button data-testid="track-submit" disabled={busy} className="w-full bg-sharoa-rose text-white text-xs uppercase tracking-widest py-4">{busy ? "Tracking…" : "Track Order"}</button>
      </form>

      {result && (
        <div className="mt-8 border border-sharoa-line p-6 sh-fade-up">
          <div className="flex justify-between items-start mb-5">
            <div><p className="text-xs uppercase tracking-widest text-sharoa-muted">Order</p><p className="font-mono text-lg">#{result.order_number}</p></div>
            <div className="text-right"><p className="text-xs uppercase tracking-widest text-sharoa-muted">Status</p><p className="font-serif text-lg text-sharoa-rose">{result.status_label}</p></div>
          </div>
          <div className="space-y-0">
            {result.steps.map((s, i) => {
              const done = i <= result.current_step;
              const current = i === result.current_step;
              return (
                <div key={s} className="flex items-center gap-3 py-2">
                  {done ? <CheckCircle2 size={20} className={current ? "text-sharoa-rose" : "text-sharoa-mauve"} /> : <Circle size={20} className="text-sharoa-line" />}
                  <span className={`text-sm ${current ? "font-semibold text-sharoa-charcoal" : done ? "text-sharoa-charcoal" : "text-sharoa-muted"}`}>{s}</span>
                </div>
              );
            })}
          </div>
          <div className="grid grid-cols-2 gap-3 text-sm border-t border-sharoa-line pt-4 mt-4">
            <div><p className="text-xs text-sharoa-muted">Courier</p><p>{result.shipment?.courier || "—"}</p></div>
            <div><p className="text-xs text-sharoa-muted">AWB / Tracking</p><p className="font-mono text-xs">{result.shipment?.awb || "—"}</p></div>
            <div><p className="text-xs text-sharoa-muted">Shipped On</p><p>{result.shipment?.ship_date || "—"}</p></div>
            <div><p className="text-xs text-sharoa-muted">Expected Delivery</p><p>{result.eta || "—"}</p></div>
          </div>
          <a href={waLink(settings?.social?.whatsapp?.number, `Hi SHAROA ♡ I need help with order #${result.order_number}.`)} target="_blank" rel="noreferrer"
            className="mt-5 flex items-center justify-center gap-2 border border-[#25D366] text-[#128C4A] text-xs uppercase tracking-widest py-3">
            <MessageCircle size={15} /> Chat with SHAROA
          </a>
        </div>
      )}
    </div>
  );
}
